// table-generic.component.ts
import { Component, Input, Output, EventEmitter, TemplateRef, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ToastrService } from 'ngx-toastr';

export interface TableColumn {
  key: string;
  title: string;
  type?: 'text' | 'number' | 'date' | 'boolean' | 'currency' | 'custom' | 'badge' | 'actions' | 'chips';
  sortable?: boolean;
  visible?: boolean;
  width?: string;
  format?: string;
  align?: 'left' | 'center' | 'right';
  badge?: {
    [key: string]: string; // Mapeo de valores a clases CSS
  };
  actions?: TableAction[];
}

export interface TableAction<T = any> {
  icon: string;
  tooltip: string;
  color?: 'primary' | 'accent' | 'warn';
  emit?: string; // Evento a emitir
  handler?: (item: T) => void; // Función personalizada
}

export interface TableConfig {
  title?: string;
  columns: TableColumn[];
  pageSize?: number;
  pageSizeOptions?: number[];
  actions?: TableAction[];
  noDataMessage?: string;
  selectable?: boolean;
  stickyHeader?: boolean;
}

@Component({
  selector: 'app-table-generic',
  templateUrl: './table-generic.component.html',
  styleUrls: ['./table-generic.component.scss']
})
export class TableGenericComponent<T = any> implements OnChanges {

  constructor(

    public dialog: MatDialog,
    public toastr: ToastrService
  ) {
  }
  @Input() config: TableConfig = {
    columns: [],
    pageSize: 10,
    pageSizeOptions: [5, 10, 25, 100],
    noDataMessage: 'No hay datos disponibles'
  };
  @Input() data: T[] = [];
  @Input() dialogComponent : any;
  @Input() totalItems = 0;
  @Input() loading = false;
  @Input() error: string | null = null;
  @Input() customTemplates: { [key: string]: TemplateRef<any> } = {};
  @Input() canEdit = true;
  @Input() canDelete = true;
  @Input() activarBusqueda = true;
  @Input() selectMode: 'single' | 'multiple' = 'single';
  @Input() serverSide = false;
  @Input() pageIndexInput = 0;
  @Input() pageSizeInput?: number;

  @Output() pageChange = new EventEmitter<PageEvent>();
  @Output() sortChange = new EventEmitter<Sort>();
  @Output() rowClick = new EventEmitter<T>();
  @Output() editClick = new EventEmitter<T>();
  @Output() deleteClick = new EventEmitter<T>();
  @Output() detailsClick = new EventEmitter<T>();
  @Output() searchClick = new EventEmitter<string>();
  @Output() selectionChange = new EventEmitter<T[]>();

  displayedColumns: string[] = [];
  selectedItem: T | null = null;
  expandedItem: T | null = null;
  selectedItems: T[] = [];
  filterTerm: string = '';
  filterValue: string = '';
  pageSize = 10;
  pageIndex = 0;
  paginatorLength = 0;
  filteredData: T[] = [];
  displayData: T[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageIndexInput'] && changes['pageIndexInput'].currentValue != null) {
      this.pageIndex = changes['pageIndexInput'].currentValue;
    }

    if (changes['pageSizeInput'] && changes['pageSizeInput'].currentValue != null) {
      this.pageSize = changes['pageSizeInput'].currentValue;
    }

    if (changes['config']) {
      this.pageSize = this.config.pageSize || this.pageSize || 10;
      if (!this.serverSide) {
        this.pageIndex = 0;
      }
    }

    const dataChanged = !!changes['data'];
    const configChanged = !!changes['config'];
    const totalChanged = !!changes['totalItems'];

    if (configChanged || dataChanged || totalChanged) {
      this.updateDisplayedColumns();
      const resetPage = !this.serverSide && dataChanged ? true : configChanged;
      this.rebuildData(resetPage);
    }
  }

  private updateDisplayedColumns(): void {
    this.displayedColumns = this.config.columns
      .filter(col => col.visible !== false)
      .map(col => col.key);

    // Solo agregar columna de acciones si no existe ya una columna de acciones
    if (!this.hasCustomActions() && (this.canEdit || this.canDelete)) {
      this.displayedColumns.push('actions');
    }
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    if (!this.serverSide) {
      this.updatePagedData();
    }
    this.pageChange.emit(event);
  }

  onSortChange(sort: Sort): void {
    const { active, direction } = sort;
    if (this.serverSide) {
      this.sortChange.emit(sort);
      return;
    }

    const column = this.config.columns.find(c => c.key === active);
    if (!column) {
      return;
    }

    if (direction === '') {
      this.rebuildData(false);
      this.sortChange.emit(sort);
      return;
    }

    const compare = (a: any, b: any) => {
      const valA = this.getCellValue(a, column);
      const valB = this.getCellValue(b, column);

      if (valA == null && valB != null) { return -1; }
      if (valA != null && valB == null) { return 1; }
      if (valA == null && valB == null) { return 0; }

      if (valA > valB) { return 1; }
      if (valA < valB) { return -1; }
      return 0;
    };

    this.filteredData = [...this.filteredData].sort((a, b) => {
      const result = compare(a, b);
      return direction === 'asc' ? result : -result;
    });

    this.updatePagedData();
    this.sortChange.emit(sort);
  }

  onEdit(item: T): void {
    this.editClick.emit(item);
  }

  onDelete(item: T): void {
    this.deleteClick.emit(item);
  }

  expandActions(item: T): void {
    this.expandedItem = this.compareItems(this.expandedItem, item) ? null : item;
  }

  isExpanded(item: T): boolean {
    return this.compareItems(this.expandedItem, item);
  }

  hasCustomActions(): boolean {
    return this.config.columns.some(col => col.type === 'actions');
  }

  getCellValue(item: any, column: TableColumn): any {
    return column.key.split('.').reduce((o, k) => o?.[k], item);
  }

  formatValue(value: any, column: TableColumn): string {
    if (value == null) return '';

    switch (column.type) {
      case 'date':
        return this.formatDate(value, column.format);
      case 'number':
        return this.formatNumber(value, column.format);
      case 'currency':
        return this.formatCurrency(value, column.format);
      case 'boolean':
        return value ? 'Sí' : 'No';
      case 'chips':
        if (Array.isArray(value)) {
          return value.length > 0 ? value.join(', ') : '';
        }
        return value.toString();
      default:
        return value.toString();
    }
  }

  private formatDate(value: string | Date, format?: string): string {
    return new Date(value).toLocaleDateString('es-MX');
  }

  private formatNumber(value: number, format?: string): string {
    return value.toLocaleString('es-MX');
  }

  private formatCurrency(value: number, format?: string): string {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);
  }

  private compareItems(a: T | null, b: T | null): boolean {
    if (!a || !b) return false;
    return (a as any).id === (b as any).id;
  }

  getColumnsToDisplay(): string[] {
    return this.displayedColumns;
  }

  getVisibleColumns(): TableColumn[] {
    return this.config.columns.filter(col => col.visible !== false);
  }

  applyFilter(term: string): void {
    const rawValue = (term || '').toString();
    this.filterValue = rawValue;
    this.filterTerm = rawValue.trim().toLowerCase();
    this.searchClick.emit(this.filterTerm);

    if (this.serverSide) {
      this.pageIndex = 0;
      this.updatePaginatorLength();
    } else {
      this.rebuildData(true);
    }
  }

  getBadgeClass(value: any, column: TableColumn): string {
    if (!column.badge) return '';
    const badgeClass = column.badge[value];
    return badgeClass ? `mat-badge-${badgeClass}` : '';
  }

  getChipValues(value: any): string[] {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.filter(item => item != null && item !== '');
    }
    if (typeof value === 'string') {
      // Si es un string separado por comas, convertirlo a array
      return value.split(',').map(item => item.trim()).filter(item => item !== '');
    }
    return [value.toString()];
  }

  onActionClick(item: T, action: TableAction): void {
    if (action.emit) {
      // Emitir evento personalizado
      switch(action.emit) {
        case 'editClick': this.editClick.emit(item); break;
        case 'deleteClick': this.deleteClick.emit(item); break;
        case 'detailsClick': this.detailsClick.emit(item); break;
        // Agrega más casos según necesites
      }
    } else if (action.handler) {
      // Ejecutar handler personalizado
      action.handler(item);
    }
  }

  private rebuildData(resetPage: boolean): void {
    const source = Array.isArray(this.data) ? [...this.data] : [];

    if (this.serverSide) {
      this.filteredData = source;
      if (resetPage) {
        this.pageIndex = 0;
      }
      this.updatePaginatorLength();
      this.displayData = [...this.filteredData];
      return;
    }

    if (this.filterTerm) {
      this.filteredData = source.filter(row => this.matchesFilter(row, this.filterTerm));
    } else {
      this.filteredData = source;
    }

    if (resetPage) {
      this.pageIndex = 0;
    } else {
      const maxPage = Math.max(Math.ceil(this.filteredData.length / this.pageSize) - 1, 0);
      if (this.pageIndex > maxPage) {
        this.pageIndex = maxPage;
      }
    }

    this.updatePaginatorLength();
    this.updatePagedData();
  }

  private updatePagedData(): void {
    const start = this.pageIndex * this.pageSize;
    const end = start + this.pageSize;
    this.displayData = this.filteredData.slice(start, end);
  }

  private updatePaginatorLength(): void {
    if (this.serverSide) {
      this.paginatorLength = this.totalItems || this.filteredData.length;
      return;
    }

    this.paginatorLength = this.filterTerm
      ? this.filteredData.length
      : (this.totalItems || this.filteredData.length);
  }

  private matchesFilter(row: any, filter: string): boolean {
    if (!row) { return false; }

    return Object.values(row).some(value => {
      if (value == null) { return false; }
      if (typeof value === 'object') {
        try {
          return JSON.stringify(value).toLowerCase().includes(filter);
        } catch {
          return false;
        }
      }
      return value.toString().toLowerCase().includes(filter);
    });
  }
}
